import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'underscore';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import { IconButton, Tooltip } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import EditIcon from '@mui/icons-material/EditOutlined';
import CircularProgress from "@mui/material/CircularProgress";
import CancelIcon from '@mui/icons-material/Cancel';
import DataTable from '../../../../components/DataTableServerSide/DataTableServerSide';
import { GetSiteDetails, GetComments, ToggleSiteTrackerDialog, ToggleDeleteSiteTrackerDialog } from '../../actions/SiteTrackerActions.js';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	infoButton: {
		color: theme.palette.info.main
	}
}));

const SiteTrackerDatatable = (props) => {
	const {
		className,
		data,
		tableName,
		changeColumnVisibility,
		visibleColumns,
		changeFilterVisibility,
		visibleFilters,
		totalCount,
		...rest
	} = props;

	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [filteredData, setFilteredData] = useState(data); // data after search filter
	const [slicedFilteredData, setSlicedFilteredData] = useState(data.slice(0, 10)); // data after search filter and pagination (only current page)
	const [rowsCount, setRowsCount] = useState(totalCount ?? data?.length ?? 0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchText, setSearchText] = useState("");
	const dispatch = useDispatch();
	const classes = useStyles();
	const userId = useSelector(state => state.session.user.id);

	const fileCopy = useSelector((state) => state.siteTrackerReducer.fileCopy);

	// useMemo(() => {
	// 	console.log('deu');
	// 	console.log(fileCopy);
	// 	if (fileCopy) {
	// 		window.open('report.html');
	// 	}
	// }, [fileCopy]);

	const _editSite = (tableId) => {
		dispatch(GetSiteDetails(userId, tableId));
		dispatch(ToggleSiteTrackerDialog(true, tableId));
	};

	const _deleteSite = (tableId) => {
		dispatch(ToggleDeleteSiteTrackerDialog(true, tableId));
	};

	const _handleStyle = (value) => {
		if (value === 'OK') {
			return {
				style: {
					backgroundColor: 'green',
					color: 'white',
					fontWeight: 'bold'
				}
			};
		} if (value === 'NOK') {
			return {
				style: {
					backgroundColor: '#dc143c',
					color: 'white',
					fontWeight: 'bold'
				}
			};
		} if (value === 'N/A') {
			return {
				style: {
					backgroundColor: 'yellow',
					color: 'black',
					fontWeight: 'bold'
				}
			};
		}
		return {
		};
	};

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;
		return (
			<>
				<div style={{ minWidth: '80px' }}>
					<Tooltip title="Edit">
						<IconButton
							edge="end"
							size="small"
							className={classes.primaryButton}
							onClick={() => _editSite(rowData[0])}
						>
							<EditIcon />
						</IconButton>
					</Tooltip>
					{/* <Tooltip title="Remove">
						<IconButton
							edge="end"
							size="small"
							className={classes.errorButton}
							onClick={() => _deleteSite(rowData[0])}
						>
							<CancelIcon />
						</IconButton>
					</Tooltip> */}
				</div>
			</>
		);
	};


	const _getTableColumns = () => (
		[
			{
				name: 'id',
				label: 'Id',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'key',
				label: 'Key',
				options: {
					display: false,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Key - ${v}`
					},
				}
			},
			{
				name: 'siteId',
				label: 'Site Id',
				options: {
					display: true,
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `siteId - ${v}`
					},
				}
			},
			{
				name: 'siteAdded',
				label: 'Site Added',
				options: {
					display: visibleColumns && visibleColumns.includes('siteAdded'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `siteAdded - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'siteAdded').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'clusterName',
				label: 'Cluster Name',
				options: {
					display: visibleColumns && visibleColumns.includes('clusterName'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Cluster Name - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'clusterName').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'mandatoryOptional',
				label: 'Mandatory Optional',
				options: {
					display: visibleColumns && visibleColumns.includes('mandatoryOptional'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Mandatory Optional - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'mandatoryOptional').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'clusterUseCase',
				label: 'Cluster Use Case',
				options: {
					display: visibleColumns && visibleColumns.includes('ClusterUseCase'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Cluster Use Case - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'ClusterUseCase').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'benefit',
				label: 'Benefit',
				options: {
					display: visibleColumns && visibleColumns.includes('benefit'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Benefit - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'benefit').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'eMocnType',
				label: 'E-MOCN Type',
				options: {
					display: visibleColumns && visibleColumns.includes('eMocnType'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `E-MOCN Type - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'eMocnType').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'eMocnSubType',
				label: 'E-MOCN Sub Type',
				options: {
					display: visibleColumns && visibleColumns.includes('eMocnSubType'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `E-MOCN Sub Type - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'eMocnSubType').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'script',
				label: 'Script',
				options: {
					display: visibleColumns && visibleColumns.includes('script'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Script - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'script').map(item => item.filterValue)[0]
				}
			},
			{
				name: '_3GsdPriority',
				label: '3GSD Priority',
				options: {
					display: visibleColumns && visibleColumns.includes('_3GsdPriority'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `3GSD Priority - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === '_3GsdPriority').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'vendor',
				label: 'Vendor',
				options: {
					display: visibleColumns && visibleColumns.includes('vendor'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Vendor - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'vendor').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'operator',
				label: 'Operator',
				options: {
					display: visibleColumns && visibleColumns.includes('operator'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Operator - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'operator').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'host',
				label: 'Host',
				options: {
					display: visibleColumns && visibleColumns.includes('host'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Host - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'host').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'longitude',
				label: 'Longitude',
				options: {
					display: visibleColumns && visibleColumns.includes('longitude'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Longitude - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'longitude').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'latitude',
				label: 'Latitude',
				options: {
					display: visibleColumns && visibleColumns.includes('latitude'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Latitude - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'latitude').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'jvMbnlId',
				label: 'JV/MBNL Id',
				options: {
					display: visibleColumns && visibleColumns.includes('jvMbnlId'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `JV/MBNL Id - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'jvMbnlId').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'd1BISReportComments',
				label: 'D1 BIS Report Comments',
				options: {
					display: visibleColumns && visibleColumns.includes('d1BISReportComments'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `D1 BIS Report Comments - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'd1BISReportComments').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'beacon',
				label: 'Beacon',
				options: {
					display: visibleColumns && visibleColumns.includes('beacon'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Beacon - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'beacon').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'unwind',
				label: 'Unwind',
				options: {
					display: visibleColumns && visibleColumns.includes('unwind'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `Unwind - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'unwind').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'mocnCategoryPlannedForTrial',
				label: 'MOCN Category Planned For Trial',
				options: {
					display: visibleColumns && visibleColumns.includes('mocnCategoryPlannedForTrial'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `MOCN Category Planned For Trial - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'mocnCategoryPlannedForTrial').map(item => item.filterValue)[0]
				}
			},
			{
				name: 'mergeCoStatus',
				label: 'MergeCo Status',
				options: {
					display: visibleColumns && visibleColumns.includes('mergeCoStatus'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `MergeCo Status - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'mergeCoStatus').map(item => item.filterValue)[0],
				}
			},
			{
				name: 'mergeCoPlan',
				label: 'MergeCo Plan',
				options: {
					display: visibleColumns && visibleColumns.includes('mergeCoPlan'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `MergeCo Plan - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'mergeCoPlan').map(item => item.filterValue)[0],
				}
			},
			{
				name: 'highMedLowConfig',
				label: 'High/Med/Low Config',
				options: {
					display: visibleColumns && visibleColumns.includes('highMedLowConfig'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `High/Med/Low Config - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'highMedLowConfig').map(item => item.filterValue)[0],
				}
			},
			{
				name: 'mocnActivationDateF',
				label: 'MOCN Activation Date (F)',
				options: {
					display: visibleColumns && visibleColumns.includes('mocnActivationDateF'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => v
							? `MOCN Activation Date (F) - ${moment(v, 'DD/MM/YYYY', true).isValid()
								? moment(v, 'DD/MM/YYYY').format('DD/MM/YYYY')
								: v
							}`
							: ''
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'mocnActivationDateF').map(item => item.filterValue)[0],
					customBodyRender: (value) => {
						return value ? moment(value, "MM/DD/YYYY HH:mm:ss").format("DD/MM/YYYY") : '';
					}
				}
			},
			{
				name: 'mocnActivationDateA',
				label: 'MOCN Activation Date (A)',
				options: {
					display: visibleColumns && visibleColumns.includes('mocnActivationDateA'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `MOCN Activation Date (A) - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'mocnActivationDateA').map(item => item.filterValue)[0],
					customBodyRender: (value) => { return value ? moment(value).format('DD/MM/YYYY') : ''; }
				}
			},
			{
				name: 'mocnActivationStatus',
				label: 'MOCN Activation Status',
				options: {
					display: visibleColumns && visibleColumns.includes('mocnActivationStatus'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `MOCN Activation Status - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'mocnActivationStatus').map(item => item.filterValue)[0],
				}
			},
			{
				name: 'mocnActivationComments',
				label: 'MOCN Activation Comments',
				options: {
					display: visibleColumns && visibleColumns.includes('mocnActivationComments'),
					filterType: 'textField',
					customFilterListOptions: {
						render: v => `MOCN Activation Comments - ${v}`
					},
					filterList: visibleFilters && visibleFilters.filter(item => item.columnName === 'mocnActivationComments').map(item => item.filterValue)[0],
				}
			},
			{
				name: 'action',
				label: 'Actions',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta),
				},

			},
		]
	);


	useEffect(() => {
		if (!data) return;
		// whenever "sites" changes, reset filteredSites
		setRowsCount(data.length);
		setFilteredData(data);
		const slicedFilteredSites = data.slice(0, 10); // initial load
		setSlicedFilteredData(slicedFilteredSites);
		setLoading(false);
	}, [data]);


	// useMemo(() => {
	// 	console.log('deu');
	// 	console.log(fileCopy);
	// 	if (fileCopy) {
	// 		window.open('report.html');
	// 	}
	// }, [fileCopy]);

	const columns = _getTableColumns();


	const onSearchChange = (text) => {
		// console.log(text);
		setSearchText(text);
		setPage(0);
	};

	const handleFilterChange = ({newSearchText, filterList = [], columns = []}) => {
		setSearchText(newSearchText ?? '');
		const filtered = data.filter(row => {
			// by serach text
			const searchFilter =  Object.values(row).some(value =>
				(value?.toString() ?? '').toLowerCase().includes((newSearchText ?? '').toLowerCase()));
			// by column filters
			const colFilter = filterList.every((columnFilters, colIndex) => {
				if (!columnFilters.length) return true;
				const cellValue = row[columns[colIndex].name];
				return columnFilters.some(filter =>
					(cellValue ?? '').toString().toLowerCase().includes((filter ?? '').toLowerCase())
				);
			});
			return searchFilter && colFilter;
		});
		setPage(0); 
		setRowsCount(filtered.length);
		setFilteredData(filtered);
		setSlicedFilteredData(filtered.slice(0, rowsPerPage)); // reset to first page
	}

	const handlePaginationChange = ({pageIndex, rowsPerPage}) => {
		setPage(pageIndex);
		setRowsPerPage(rowsPerPage);
		const slicedFiltered = filteredData.slice(pageIndex * rowsPerPage, (pageIndex + 1) * rowsPerPage);
		setSlicedFilteredData(slicedFiltered);
	};

	const handleSortChange = ({column, direction}) => {
		const sorted = [...filteredData].sort((a, b) => {
			if (a[columns[column].name] < b[columns[column].name]) {
				return direction === 'asc' ? -1 : 1;
			} else if (a[columns[column].name] > b[columns[column].name]) {
				return direction === 'asc' ? 1 : -1;
			}
			return 0;
		});
		setFilteredData(filteredData);
		setPage(0); 
		setRowsCount(filteredData.length);
		setSlicedFilteredData(sorted.slice(0, rowsPerPage)); // reset to first page
	};

	const _getTableOptions = () => ({
		filterType: 'dropdown',
		selectableRows: 'none',
		count: rowsCount, // total de registos
		page: page,
		rowsPerPage: rowsPerPage,
		textLabels: {
			body: {
				noMatch: loading ? (
					<CircularProgress size={40} />
				) : (
					"Sorry, no matching records found"
				),
			},
		},

		onViewColumnsChange: (changedColumn, action) => {
			const changeColumn = { userId, tableName, columnName: changedColumn, action };
			changeColumnVisibility(changeColumn);
		},
		// onFilterChange: handleFilterChange,
		onSearchChange: onSearchChange,
		// customSearchRender: (_searchText, handleSearch, hideSearch) => {
		// 	return (
		// 		<TextField
		// 			autoFocus
		// 			fullWidth
		// 			value={searchText}
		// 			onChange={(e) => setSearchText(e.target.value)} // local state only
		// 			onKeyDown={(e) => {
		// 				if (e.key === "Enter") {
		// 					handleSearch(searchText); // ✅ trigger search only on Enter
		// 				}
		// 			}}
		// 			placeholder="Press Enter to search"
		// 			variant="standard"
		// 		/>
		// 	);
		// },
		onTableChange: (action, tableState) => {
			switch (action) {
				case "changePage":
				case "changeRowsPerPage":
					handlePaginationChange({
						pageIndex: tableState.page,
						rowsPerPage: tableState.rowsPerPage,
					});
					break;
				case "search":
				case "filterChange":
				case "resetFilters":
					handleFilterChange({
						newSearchText: tableState.searchText,
						filterList: tableState.filterList,
						columns: tableState.columns, // contains column info
					});
					break;
				case "sort":
					handleSortChange({
						column: tableState.activeColumn,
						direction: tableState.announceText.includes("descending")
						? "desc"
						: "asc",
					});
					break;
				default:
					break;
			}
		}

	});

	const componentMapping = {
		Skeleton,
	};

	const renderComponentCell = (componentString) => {
		const Component = componentMapping[componentString];
		return Component ? <Component /> : null;
	};

	let columnsSchema = '';
	const getColumns = (el) => {
		columnsSchema = `${columnsSchema}"${el.name}": "Skeleton", `;
	};

	_getTableColumns().forEach(element => getColumns(element));

	columnsSchema = columnsSchema.slice(0, -2);
	const parsed = JSON.parse(`{${columnsSchema}}`);
	const novo = [];

	let i = 1;
	while (i <= 5) {
		novo.push(parsed);

		i++;
	}

	let updatedData = [];
	if (data && data.length > 0) {
		updatedData = novo.slice(0, 10).map((item) => {
			const updatedItem = {};

			Object.keys(item).forEach((key) => {
				updatedItem[key] = renderComponentCell('Skeleton');
			});

			return updatedItem;
		});
	}

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<DataTable
				id="voiceDataTble"
				title=""
				data={slicedFilteredData}
				columns={_getTableColumns()}
				rowsPerPage={10}
				tableBodyMaxHeight="38em"
				tableBodyCellMaxWidth="250"
				options={_getTableOptions()}
			/>
		</div>
	);
};

SiteTrackerDatatable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
	tableName: PropTypes.string,
	changeColumnVisibility: PropTypes.func,
	visibleColumns: PropTypes.array,
	changeFilterVisibility: PropTypes.func,
	visibleFilters: PropTypes.array,
	totalCount: PropTypes.number.isRequired,
};

export default SiteTrackerDatatable;
